import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Camera, CheckCircle2, ExternalLink, RefreshCw, Sparkles, XCircle } from "lucide-react";
import { API } from "../config";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

const StatusCard = ({ st, onRun, running }) => {
  const cap = st.last_capture;
  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6 grid md:grid-cols-3 gap-4" data-testid="manuals-status-card">
      <div>
        <p className="text-xs text-gray-400">Ultimo aggiornamento mostrato nei manuali</p>
        <p className="text-white text-lg font-semibold mt-1" data-testid="manuals-last-check">{fmt(st.last_check_at)}</p>
      </div>
      <div>
        <p className="text-xs text-gray-400">Ultimi screenshot automatici</p>
        <p className="text-white text-sm mt-1" data-testid="manuals-last-capture">
          {cap ? `${fmt(cap.at)} · ${cap.captured} acquisiti · ${cap.changed} cambiati${cap.failed ? ` · ${cap.failed} non riusciti` : ""}` : "Non ancora eseguiti"}
        </p>
        {cap?.errors?.length > 0 && <p className="text-xs text-amber-400 mt-1 truncate" title={cap.errors.join("\n")}>{cap.errors[0]}</p>}
      </div>
      <div className="flex md:justify-end items-start gap-2 flex-wrap">
        <a href="/dealer/manual" target="_blank" rel="noreferrer" className="btn-lojack-outline text-sm flex items-center gap-1" data-testid="manuals-open-dealer">
          <ExternalLink size={14} /> Apri manuale
        </a>
        <button onClick={onRun} disabled={running} className="btn-lojack-outline text-sm flex items-center gap-1" data-testid="manuals-run-now">
          <Camera size={14} /> {running ? "Avviato…" : "Aggiorna ora"}
        </button>
      </div>
    </div>
  );
};

const ProposalForm = ({ onCreated, generating }) => {
  const [text, setText] = useState("");
  const submit = async () => {
    try {
      await axios.post(`${API}/admin/operational-manuals/proposals`, { changes_text: text });
      toast.success("Proposta in preparazione: compare qui sotto tra circa un minuto");
      setText("");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || "Invio non riuscito"); }
  };
  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="manuals-proposal-form">
      <p className="flex items-center gap-2 text-white font-semibold"><Sparkles size={18} /> Proponi un aggiornamento del testo</p>
      <p className="text-xs text-gray-400 mt-1 mb-3">Descrivi cosa è cambiato nella piattaforma: l'AI prepara la modifica dei paragrafi interessati, che poi va approvata.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="manuals-proposal-text"
        placeholder="Es. Nel form Nuovo Cliente è stato aggiunto il campo 'Data di consegna del veicolo'"
        className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-gray-500" />
      <button onClick={submit} disabled={text.trim().length < 10 || generating} className="btn-lojack mt-3 text-sm" data-testid="manuals-proposal-submit">
        {generating ? "Preparazione in corso…" : "Prepara proposta"}
      </button>
    </div>
  );
};

const ProposalCard = ({ p, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(p.summary || "");
  const act = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/operational-manuals/proposals/${p.id}/${action}`, action === "approve" ? { summary } : undefined);
      toast.success(action === "approve" ? "Approvata: manuale, traduzioni e PDF si aggiornano in tutte le lingue" : "Proposta rifiutata");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
    setBusy(false);
  };
  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid={`manuals-proposal-${p.id}`}>
      <div className="flex justify-between flex-wrap gap-3 mb-3">
        <div>
          <p className="text-white font-semibold">{p.role_label}</p>
          <p className="text-xs text-gray-400">{fmt(p.created_at)} · {p.source === "changelog" ? "da aggiornamento piattaforma" : `richiesta di ${p.created_by}`}</p>
          <p className="text-xs text-gray-300 mt-2 whitespace-pre-line">{p.changes_text}</p>
        </div>
        <div className="flex gap-2 items-start">
          <button onClick={() => act("approve")} disabled={busy} className="btn-lojack text-sm flex items-center gap-1" data-testid={`manuals-approve-${p.id}`}>
            <CheckCircle2 size={14} /> Approva
          </button>
          <button onClick={() => act("reject")} disabled={busy} className="btn-lojack-outline text-sm flex items-center gap-1" data-testid={`manuals-reject-${p.id}`}>
            <XCircle size={14} /> Rifiuta
          </button>
        </div>
      </div>
      <label className="block text-xs text-gray-400 mb-1">Riepilogo delle novità mostrato ai dealer (tradotto in automatico nella loro lingua)</label>
      <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} data-testid={`manuals-summary-${p.id}`}
        className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-gray-500 mb-3" />
      <div className="manual-diff text-xs font-mono bg-black rounded-xl p-3 max-h-96 overflow-auto" data-testid={`manuals-diff-${p.id}`}
        dangerouslySetInnerHTML={{ __html: p.diff_html }} />
    </div>
  );
};

export default function OperationalManualsAdminPage() {
  const [st, setSt] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      axios.get(`${API}/admin/operational-manuals`),
      axios.get(`${API}/admin/operational-manuals/proposals`),
    ]);
    setSt(a.data); setProposals(b.data.proposals);
  }, []);

  useEffect(() => { load().catch(() => toast.error("Caricamento non riuscito")); }, [load]);
  useEffect(() => {
    if (!st?.generating) return undefined;
    const id = setInterval(() => load().catch(() => {}), 8000);
    return () => clearInterval(id);
  }, [st?.generating, load]);

  const runNow = async () => {
    setRunning(true);
    try { await axios.post(`${API}/admin/operational-manuals/run-now`); toast.success("Aggiornamento avviato: screenshot e PDF pronti in qualche minuto"); }
    catch { toast.error("Avvio non riuscito"); }
    setTimeout(() => { setRunning(false); load().catch(() => {}); }, 60000);
  };

  if (!st) return <div className="p-8 text-gray-400">Caricamento…</div>;
  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6" data-testid="operational-manuals-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3"><BookOpen /> Manuali Operativi</h1>
        <button onClick={() => load()} className="text-gray-400 hover:text-white" data-testid="manuals-refresh"><RefreshCw size={16} /></button>
      </div>
      <p className="text-sm text-gray-400 -mt-3">Ogni notte gli screenshot vengono rifatti sulla piattaforma reale con dati demo e la data di ultimo aggiornamento si aggiorna. Le modifiche al testo vanno approvate qui.</p>
      <StatusCard st={st} onRun={runNow} running={running} />
      <ProposalForm onCreated={() => setTimeout(() => load().catch(() => {}), 1500)} generating={st.generating} />
      <div className="space-y-4">
        <p className="text-white font-semibold" data-testid="manuals-pending-count">Proposte da approvare ({proposals.length})</p>
        {proposals.length === 0 && <p className="text-sm text-gray-500" data-testid="manuals-no-proposals">Nessuna proposta in attesa.</p>}
        {proposals.map((p) => <ProposalCard key={p.id} p={p} onDone={load} />)}
      </div>
      {st.screenshots.length > 0 && (
        <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="manuals-screenshots">
          <p className="text-white font-semibold mb-3">Screenshot attuali (italiano)</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {st.screenshots.map((s) => (
              <a key={s.name} href={s.url} target="_blank" rel="noreferrer" data-testid={`manuals-shot-${s.name}`}>
                <img src={s.url} alt={s.name} className="rounded-lg border border-gray-800 w-full" />
                <p className="text-xs text-gray-400 mt-1">{s.name}</p>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
